// Champions ledger — champion-role.md §7.1.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ChampionRow, type RosterRow } from "../lib/api";

const FUNCTIONS = ["Engineering","Sales","HR","Finance","Project Management","Operations","Other"];
const STATUSES = ["Active","Vacant — Strategist holding","Term ending — successor TBD","Renewed","Stepped down"];

export function Champions() {
  const qc = useQueryClient();
  const { data } = useQuery<ChampionRow[]>({ queryKey: ["champions"], queryFn: api.champions.list });
  const roster = useQuery<RosterRow[]>({ queryKey: ["roster"], queryFn: api.roster.list });

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    function: "Engineering", champion_id: "", term_start: "", term_end: "", status: "Active",
  });

  const create = useMutation({
    mutationFn: () => api.champions.create({ ...form, champion_id: form.champion_id || undefined } as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["champions"] });
      setAdding(false);
    },
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.champions.update(id, { status } as any),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["champions"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Champions</h1>
        <button
          className="px-3 py-1 rounded bg-blue-600 text-white text-sm"
          onClick={() => setAdding((s) => !s)}
        >{adding ? "Cancel" : "Add champion"}</button>
      </div>

      {adding ? (
        <form
          className="border rounded p-3 bg-white space-y-2"
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-600">Function</label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={form.function} onChange={(e) => setForm({ ...form, function: e.target.value })}>
                {FUNCTIONS.map((f) => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Champion (or leave empty for vacant)</label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={form.champion_id} onChange={(e) => setForm({ ...form, champion_id: e.target.value })}>
                <option value="">— vacant —</option>
                {(roster.data ?? []).map((r) =>
                  <option key={r.id} value={r.id}>{r.name} ({r.function})</option>
                )}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Term start</label>
              <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.term_start} onChange={(e) => setForm({ ...form, term_start: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-slate-600">Term end</label>
              <input type="date" className="w-full border rounded px-2 py-1 text-sm" value={form.term_end} onChange={(e) => setForm({ ...form, term_end: e.target.value })} />
            </div>
          </div>
          {create.error ? <p className="text-red-700 text-sm">{(create.error as Error).message}</p> : null}
          <button className="px-3 py-1 rounded bg-blue-600 text-white text-sm">Add</button>
        </form>
      ) : null}

      <table className="w-full text-sm">
        <thead className="border-b border-slate-300 text-left">
          <tr>
            <th className="py-2">Function</th>
            <th>Champion</th>
            <th>Term</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((c) => (
            <tr key={c.id} className="border-b border-slate-100">
              <td className="py-2 font-medium">{c.function}</td>
              <td>{c.champion_name || <span className="italic text-slate-500">vacant</span>}</td>
              <td className="text-slate-600">{c.term_start} → {c.term_end || "—"}</td>
              <td>
                <select className="text-xs border rounded px-1 py-0.5"
                  value={c.status}
                  onChange={(e) => setStatus.mutate({ id: c.id, status: e.target.value })}
                >
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
          {(data ?? []).length === 0 ? (
            <tr><td colSpan={4} className="py-4 text-center text-slate-500">No champions yet.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
